import { DefaultLocation, DefaultMapOptions, DefaultZoom } from 'constant';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMapsLoader } from '../GoogleMapsLoader';
import {
  GoogleMap as GoogleMapComponent,
  MarkerF,
} from '@react-google-maps/api';
import { IGoogleMapComponentProps } from './types';

export const GoogleMap: FC<IGoogleMapComponentProps> = ({
  location = DefaultLocation,
  zoom = DefaultZoom,
  handleZoomChanged,
  height = '400px',
  width = '100%',
  onMarkerDragEnd,
  onMapClick,
  draggable = true,
  showMarker = true,
  canClickMap = false,
  defaultOptions = DefaultMapOptions,
  children,
  loader,
}) => {
  const [markerPosition, setMarkerPosition] = useState(location);
  const mapRef = useRef<google.maps.Map | null>(null);

  // Keep markerPosition in sync if center prop changes externally
  useEffect(() => {
    setMarkerPosition(location ?? DefaultLocation);
  }, [location]);

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    mapRef.current = mapInstance;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleMarkerDragEnd = useCallback(
    (event: google.maps.MapMouseEvent) => {
      const lat = event.latLng?.lat();
      const lng = event.latLng?.lng();
      if (lat !== undefined && lng !== undefined) {
        setMarkerPosition?.({ lat, lng });
        onMarkerDragEnd?.(lat, lng);
      }
    },
    [onMarkerDragEnd],
  );

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      const lat = event.latLng?.lat();
      const lng = event.latLng?.lng();
      if (lat !== undefined && lng !== undefined) {
        setMarkerPosition?.({ lat, lng });
        onMapClick?.(lat, lng);
      }
    },
    [onMapClick],
  );

  const containerStyle = useMemo(() => ({ height, width }), [height, width]);

  return (
    <GoogleMapsLoader loader={loader}>
      <GoogleMapComponent
        mapContainerStyle={containerStyle}
        center={markerPosition}
        zoom={zoom}
        onZoomChanged={() => handleZoomChanged?.(zoom)}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={canClickMap ? handleMapClick : undefined}
        options={defaultOptions}
      >
        {showMarker && (
          <MarkerF
            position={markerPosition}
            draggable={draggable}
            onDragEnd={draggable ? handleMarkerDragEnd : undefined}
          />
        )}
        {children}
      </GoogleMapComponent>
    </GoogleMapsLoader>
  );
};
